/**
 * Event priority levels for processing order
 */
export var EventPriority;
(function (EventPriority) {
    EventPriority[EventPriority["CRITICAL"] = 0] = "CRITICAL";
    EventPriority[EventPriority["HIGH"] = 1] = "HIGH";
    EventPriority[EventPriority["NORMAL"] = 2] = "NORMAL";
    EventPriority[EventPriority["LOW"] = 3] = "LOW";
})(EventPriority || (EventPriority = {}));
/**
 * Move completed command for processing post-move effects
 */
export class MoveCompletedCommand {
    reanimationManager;
    reanimationEnabled;
    priority = EventPriority.NORMAL;
    eventType = 'MoveCompletedEvent';
    constructor(reanimationManager, reanimationEnabled) {
        this.reanimationManager = reanimationManager;
        this.reanimationEnabled = reanimationEnabled;
    }
    canExecute() {
        return this.reanimationEnabled;
    }
    async execute() {
        this.reanimationManager.checkReanimationOpportunities();
    }
}
/**
 * Player choice processing command
 */
export class PlayerChoiceCommand {
    response;
    playerChoiceHandler;
    eventBus;
    updateGameStatus;
    priority = EventPriority.HIGH;
    eventType = 'PlayerChoiceResponse';
    constructor(response, playerChoiceHandler, eventBus, updateGameStatus) {
        this.response = response;
        this.playerChoiceHandler = playerChoiceHandler;
        this.eventBus = eventBus;
        this.updateGameStatus = updateGameStatus;
    }
    canExecute() {
        return this.response.requestId.length > 0;
    }
    async execute() {
        const result = await this.playerChoiceHandler.processChoice(this.response);
        if (result.success) {
            this.updateGameStatus('IN_PROGRESS');
            this.eventBus.emit('CHECK_GAME_END', { reason: 'choice-processed' });
        }
    }
}
/**
 * GameEventProcessor - Handles game events with Command pattern
 *
 * Key responsibilities:
 * - Process game events from EventBus using Command pattern
 * - Support event replay for debugging
 * - Manage event side effects and coordination
 * - Process player choices and post-move reanimation
 *
 * Captures, chains included, are resolved synchronously by MoveExecutor during
 * the move; nothing here captures.
 */
export class GameEventProcessor {
    eventBus;
    reanimationManager;
    playerChoiceHandler;
    config;
    gameStatusUpdater;
    eventSubscriptions = [];
    eventHistory = [];
    maxHistorySize = 1000;
    constructor(eventBus, reanimationManager, playerChoiceHandler, config, gameStatusUpdater) {
        this.eventBus = eventBus;
        this.reanimationManager = reanimationManager;
        this.playerChoiceHandler = playerChoiceHandler;
        this.config = config;
        this.gameStatusUpdater = gameStatusUpdater;
        this.subscribeToEvents();
    }
    /**
     * Subscribe to all relevant game events
     */
    subscribeToEvents() {
        // Simple event subscriptions for non-domain events.
        // The string overload returns a bare unsubscribe function rather than an
        // EventSubscription, which is why this one was never collected and so was
        // never released by dispose() — it is wrapped to join the same list, so a
        // replaced processor stops driving status updates into a dead orchestrator.
        const unsubscribeChoiceRequest = this.eventBus.subscribe('CHOICE_REQUEST', () => {
            this.gameStatusUpdater('AWAITING_CHOICE');
        });
        this.eventSubscriptions.push({ unsubscribe: unsubscribeChoiceRequest });
    }
    /**
     * Process player choice response
     */
    async processPlayerChoice(response) {
        const command = new PlayerChoiceCommand(response, this.playerChoiceHandler, this.eventBus, this.gameStatusUpdater);
        await this.executeCommand(command);
    }
    /**
     * Handle move completion with all side effects
     */
    async handleMoveCompleted() {
        const command = new MoveCompletedCommand(this.reanimationManager, this.config.reanimationEnabled);
        await this.executeCommand(command);
    }
    /**
     * Execute a single command immediately
     */
    async executeCommand(command) {
        if (!command.canExecute()) {
            return;
        }
        try {
            await command.execute();
        }
        catch (error) {
            console.error(`[GameEventProcessor] Error executing ${command.eventType}:`, error);
        }
    }
    /**
     * Add event to replay history
     */
    addToHistory(event) {
        this.eventHistory.push(event);
        if (this.eventHistory.length > this.maxHistorySize) {
            this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
        }
    }
    /**
     * Replay events for debugging
     */
    getEventHistory() {
        return [...this.eventHistory];
    }
    /**
     * Get events of specific type from history
     */
    getEventsOfType(eventType) {
        return this.eventHistory.filter((event) => event.constructor.name === eventType.name);
    }
    /**
     * Clear event history
     */
    clearHistory() {
        this.eventHistory = [];
    }
    /**
     * Cleanup subscriptions
     */
    dispose() {
        this.eventSubscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
        this.eventSubscriptions = [];
        this.eventHistory = [];
    }
}
//# sourceMappingURL=GameEventProcessor.js.map